import voca from 'voca';

export default async function (editor, opts = {}) {
  const domc = editor.DomComponents;
  const defaultType = domc.getType('default');
  const defaultModel = defaultType.model;
  const defaultView = defaultType.view;
  const { projectId, collections, socketPlugin } = opts;

  domc.addType('socket-alert', {
    model: defaultModel.extend(
      {
        defaults: {
          ...defaultModel.prototype.defaults,
          icon: '<i class="wb webconnect-lightning-charge-fill"></i>',
          name: 'Socket Alert',
          droppable: ':not(form)',
          draggable: ':not(form)',
          collectionName: '',
          projectId,
          resizable: true,
          traits: [...defaultModel.prototype.defaults.traits],
          script: function () {
            let socketAlertAction = '{[ data-socket-alert-action ]}';
            if (typeof renderSocketAlert !== 'undefined') {
              renderSocketAlert(this, socketAlertAction);
            }
          },
        },

        async init() {
          const { setting } = socketPlugin;
          const { socketCollections } = setting;

          const collectionOptions = [
            { value: '', label: '- Select One -' },
            ...collections
              .filter((collection) => socketCollections.includes(collection.collectionName))
              .map((collection) => ({
                value: collection.collectionName,
                label: collection.collectionTitle,
              })),
          ];

          const getComponentOptions = () => {
            const wrapperComponents = editor.DomComponents.getWrapper().components().models;
            return [
              { value: '', label: '- Select One -' },
              ...wrapperComponents
                .filter((comp) => comp.getId && comp.getId())
                .map((comp) => {
                  const id = comp.getId();
                  const name = comp.get('custom-name') || comp.get('name') || comp.get('type');
                  const kebabName = voca.kebabCase(voca.decapitalize(name));
                  return {
                    value: `${kebabName}:${id}`,
                    label: `${name} (${id})`,
                  };
                }),
            ];
          };

          const buildTraits = (action = '', trigger = '') => {
            const traits = [
              ...domc.getType('default').model.prototype.defaults.traits,
              {
                label: 'Collection',
                name: 'data-socket-alert-collection',
                title: 'collection',
                type: 'select',
                options: collectionOptions,
              },
              {
                label: 'Socket Trigger',
                name: 'data-socket-alert-trigger',
                title: 'socketEvent',
                type: 'select',
                options: [
                  { value: '', label: '- Select One -' },
                  { value: 'add', label: 'On Data Add' },
                  { value: 'delete', label: 'On Data Delete' },
                ],
                changeProp: 1,
              },
              {
                label: 'Alert Type',
                name: 'data-socket-alert-type',
                title: 'alertType',
                type: 'select',
                options: [
                  { value: '', label: '- Select One -' },
                  { value: 'primary', label: 'Primary' },
                  { value: 'secondary', label: 'Secondary' },
                  { value: 'success', label: 'Success' },
                  { value: 'danger', label: 'Danger' },
                  { value: 'warning', label: 'Warning' },
                  { value: 'info', label: 'Info' },
                  { value: 'light', label: 'Light' },
                  { value: 'dark', label: 'Dark' },
                ],
                changeProp: 1,
              },
              {
                label: 'Action',
                name: 'data-socket-alert-action',
                title: 'action',
                type: 'select',
                options: [
                  { value: '', label: '- Select One -' },
                  { value: 'reload', label: 'Reload Page' },
                  { value: 'refresh', label: 'Refresh Component' },
                ],
                changeProp: 1,
              },
            ];
            if (action === 'refresh') {
              traits.push({
                label: 'Component',
                name: 'data-socket-alert-target-component',
                type: 'select',
                options: getComponentOptions(),
              });
              if (trigger !== 'delete') {
                traits.push({
                  label: 'Append At',
                  name: 'data-socket-alert-target-append',
                  type: 'select',
                  options: [
                    { value: '', label: '- Select One -' },
                    { value: 'Beginning', label: 'Beginning' },
                    { value: 'End', label: 'End' },
                  ],
                });
              }
            }
            return traits;
          };
          const currentAction = this.get('data-socket-alert-action') || '';
          const currentTrigger = this.get('data-socket-alert-trigger') || '';
          this.set('traits', buildTraits(currentAction, currentTrigger));
          this.listenTo(this, 'change:data-socket-alert-action', () => {
            const action = this.get('data-socket-alert-action');
            const trigger = this.get('data-socket-alert-trigger') || '';
            this.set('traits', buildTraits(action, trigger));
          });
          this.listenTo(this, 'change:data-socket-alert-trigger', () => {
            const action = this.get('data-socket-alert-action') || '';
            const trigger = this.get('data-socket-alert-trigger') || '';
            this.set('traits', buildTraits(action, trigger));
            this.addAttributes({
              'data-socket-alert-trigger': trigger,
            });
          });
          editor.on('load', () => {
            const action = this.get('data-socket-alert-action') || '';
            const trigger = this.get('data-socket-alert-trigger') || '';
            this.set('traits', buildTraits(action, trigger));
          });
        },
      },
      {
        isComponent(el) {
          if (el.getAttribute && el.getAttribute('data-gjs-type') === 'socket-alert') {
            return { type: 'socket-alert' };
          }
        },
      },
    ),

    view: defaultView.extend({
      events: {
        submit(e) {
          e.preventDefault();
        },
      },
      init({ model }) {
        this.listenTo(model, 'change:content', this.updateContent);
        this.listenTo(model, 'change:data-socket-alert-collection', this.updateScript);
        this.listenTo(model, 'change:data-socket-alert-type', this.updateClass);
        this.listenTo(model, 'change:data-socket-alert-event', this.updateScript);
        this.listenTo(model, 'change:data-socket-alert-trigger', this.updateScript);
      },
      updateContent() {
        if (this.model.get('content')) {
          this.el.innerHTML = this.model.get('content');
        }
      },
      updateClass() {
        const alertType = this.model.get('data-socket-alert-type') || 'primary';
        const el = this.el;
        if (el && el.classList.contains('alert')) {
          el.classList.forEach((cls) => {
            if (cls.startsWith('alert-') && cls !== 'alert') {
              el.classList.remove(cls);
            }
          });
          el.classList.add(`alert-${alertType}`);
        }

        const currentClasses = this.model.getClasses() || [];
        const filtered = currentClasses.filter(
          (cls) => !cls.startsWith('alert-') || cls === 'alert',
        );
        this.model.setClass([...filtered, `alert-${alertType}`]);
      },
    }),
  });
}
